import type { Color, PieceSymbol } from "chess.js";

// Piece drawings shared by the board (PieceArt) and the build-time social images.

export type PieceFinish = "ivory" | "wood" | "marble" | "metal";
export type PieceStyle = "staunton" | "modern";

export type Palette = {
  light: string;
  middle: string;
  dark: string;
  edge: string;
  reflection: string;
  carving: string;
};

export const FINISHES: Record<PieceFinish, Record<Color, Palette>> = {
  ivory: {
    w: { light: "#fffef5", middle: "#ece1c9", dark: "#b8a27e", edge: "#665840", reflection: "#ffffff", carving: "#8a7250" },
    b: { light: "#726860", middle: "#342b25", dark: "#1a1512", edge: "#100c09", reflection: "#d5c5b4", carving: "#090604" },
  },
  wood: {
    w: { light: "#f5dca8", middle: "#d6ac70", dark: "#9e693b", edge: "#624326", reflection: "#fff3d7", carving: "#80542f" },
    b: { light: "#976648", middle: "#573824", dark: "#2c1b13", edge: "#1c110c", reflection: "#d9ac7d", carving: "#231209" },
  },
  marble: {
    w: { light: "#ffffff", middle: "#eee8dd", dark: "#b1a99d", edge: "#6e6257", reflection: "#ffffff", carving: "#918476" },
    b: { light: "#8c7a69", middle: "#514239", dark: "#30251f", edge: "#201813", reflection: "#d9c8b7", carving: "#241a13" },
  },
  metal: {
    w: { light: "#fff1c3", middle: "#e0bf7b", dark: "#a17b38", edge: "#6a512c", reflection: "#fff8e5", carving: "#8b6c39" },
    b: { light: "#91a1ae", middle: "#4b5b68", dark: "#263039", edge: "#152029", reflection: "#d8e6f2", carving: "#1e2c36" },
  },
};

export type Sculpture = { outline: string; carving: string; highlight: string };

// The silhouette remains legible at small sizes; surface detail is deliberately
// quieter than the outline. Every piece is drawn rather than font-dependent.
export const STAUNTON: Record<PieceSymbol, Sculpture> = {
  p: {
    outline: "M50 20C43 20 38 25 38 32C38 37 41 41 45 43L42 48H58L55 43C59 41 62 37 62 32C62 25 57 20 50 20ZM43 48C43 58 40 65 34 71L33 76H67L66 71C60 65 57 58 57 48Z",
    carving: "M40 47H60M37 69Q50 72 63 69M34 75H66",
    highlight: "M44 26C39 31 41 36 43 38M47 53C46 60 43 65 42 67",
  },
  n: {
    outline: "M29 76L29 68C30 60 36 54 41 48L37 43L29 49C26 51 22 49 22 45L25 35L38 24L44 15L48 24C60 22 68 29 72 40C75 49 74 63 74 76Z",
    carving: "M48 27C61 29 67 39 66 52C65 59 62 65 60 69M29 40L33 39M33 47L40 42M48 36L42 41M32 69H69",
    highlight: "M36 31L42 27M34 60C38 54 44 51 47 45M53 28C60 31 64 36 65 41",
  },
  b: {
    outline: "M50 13A4 4 0 1 0 50 21A4 4 0 1 0 50 13ZM50 20C46 23 35 33 35 41C35 47 40 51 45 53L43 57C43 63 38 68 33 72V76H67V72C62 68 57 63 57 57L55 53C60 51 65 47 65 41C65 33 54 23 50 20Z",
    carving: "M56 29L45 43M41 55H59M37 69Q50 72 63 69M34 75H66",
    highlight: "M47 27C40 34 38 39 40 44M47 60L43 66",
  },
  r: {
    outline: "M27 21H38V30H45V21H55V30H62V21H73V40L65 47L63 65L69 72V76H31V72L37 65L35 47L27 40Z",
    carving: "M28 39H72M36 47H64M38 64H62M33 72H67M45 49L44 62M55 49L56 62",
    highlight: "M31 25V36M40 50L41 61M37 70H61",
  },
  q: {
    outline: "M27 16A4 4 0 1 0 27 24A4 4 0 1 0 27 16ZM41 14A4 4 0 1 0 41 22A4 4 0 1 0 41 14ZM50 9A4 4 0 1 0 50 17A4 4 0 1 0 50 9ZM59 14A4 4 0 1 0 59 22A4 4 0 1 0 59 14ZM73 16A4 4 0 1 0 73 24A4 4 0 1 0 73 16ZM28 24L38 39L41 23L47 38L50 18L53 38L59 23L62 39L72 24L65 49L59 53C58 61 61 67 67 72V76H33V72C39 67 42 61 41 53L35 49Z",
    carving: "M35 45Q50 49 65 45M36 50Q50 54 64 50M42 57H58M37 69Q50 72 63 69M34 75H66",
    highlight: "M33 29L37 41M44 57C44 62 42 66 40 68M39 46Q50 49 59 47",
  },
  k: {
    outline: "M46 8H54V16H62V24H54V33H46V24H38V16H46ZM34 36Q50 29 66 36L63 46L58 51C57 59 61 67 68 72V76H32V72C39 67 43 59 42 51L37 46Z",
    carving: "M37 43Q50 47 63 43M41 51H59M42 56H58M37 69Q50 72 63 69M33 75H67",
    highlight: "M48 11V19H41M38 37Q47 34 56 36M46 57C45 62 42 66 40 68",
  },
};

export const MODERN: Record<PieceSymbol, Sculpture> = {
  p: {
    outline: "M50 20A12 12 0 1 0 50 44A12 12 0 1 0 50 20ZM44 47H56L66 76H34Z",
    carving: "M43 48H57M37 70H63",
    highlight: "M43 27C40 31 42 36 44 38M46 51L41 68",
  },
  n: {
    outline: "M29 76L33 59L44 48L33 48L23 42L29 30L46 20L49 12L58 23L65 28L74 48L70 76Z",
    carving: "M49 26L60 34L64 48L54 65M33 48L44 40M37 70H65M29 39L34 39",
    highlight: "M32 32L43 26M38 59L48 49M54 28L61 35",
  },
  b: {
    outline: "M50 13C45 20 35 31 35 39C35 46 39 50 45 52L34 76H66L55 52C61 50 65 46 65 39C65 31 55 20 50 13Z",
    carving: "M57 27L45 40M42 57H58M37 70H63",
    highlight: "M47 22C40 31 38 37 40 42M46 56L41 68",
  },
  r: {
    outline: "M27 22H38V30H45V22H55V30H62V22H73V41L64 47L64 65L71 76H29L36 65V47L27 41Z",
    carving: "M28 41H72M36 47H64M36 65H64M45 48V63M55 48V63",
    highlight: "M31 26V37M40 49V62M36 71H64",
  },
  q: {
    outline: "M27 20L39 34L41 15L50 30L59 15L61 34L73 20L66 47L57 54L68 76H32L43 54L34 47Z",
    carving: "M34 44H66M41 51H59M40 60H60M36 70H64",
    highlight: "M32 28L36 40M45 56L39 68M42 22L45 32",
  },
  k: {
    outline: "M45 9H55V18H64V27H55V38H66L60 51L69 76H31L40 51L34 38H45V27H36V18H45Z",
    carving: "M36 42H64M41 51H59M38 66H62M34 74H66",
    highlight: "M48 12V21H40M48 30V37M44 55L40 65",
  },
};

export const CLASSIC_BASE = "M32 73C30 73 29 75 29 77V80L25 84V89C39 94 61 94 75 89V84L71 80V77C71 75 70 73 68 73Z";
export const MODERN_BASE = "M32 74H68L74 83V89C59 93 41 93 26 89V83Z";
